import { useState } from "react";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useMonthSalaryStore } from "web-apps/company/stores/monthSalaryStore/monthSalaryStore";

export const usePDFViewer = () => {
  const [companyState] = useCompanyStore();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [_state, monthSalaryActions] = useMonthSalaryStore();

  const viewFile = async (endpoint: any, type: string) => {
    if (!companyState.company?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await endpoint;

      const blob = new Blob([response.data], { type });
      const url = window.URL.createObjectURL(blob);

      if (fileUrl) window.URL.revokeObjectURL(fileUrl);

      setFileUrl(url);
      setFileContent(blob);
      setIsLoading(false);

      return url;
    } catch (error) {
      console.error("Failed to fetch file:", error);
      setError("Failed to load file");
      setIsLoading(false);
      return null;
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;

    document.body.appendChild(a);
    a.click();

    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const prepareExport = async (
    month: number,
    type: "SIE" | "Hogia",
    year: number
  ) => {
    try {
      const companyId = companyState.company?.id;
      if (!companyId) {
        throw { errorMessage: "MissingCompany" };
      }

      const res =
        type === "Hogia"
          ? await monthSalaryActions.generateHogiaFile(companyId, year, month)
          : await monthSalaryActions.generateSIEFile(companyId, year, month);

      const blob: Blob =
        res.data instanceof Blob
          ? res.data
          : new Blob([res.data], {
              type: type === "Hogia" ? "application/zip" : "text/plain",
            });

      const ext = type === "Hogia" ? "zip" : "se";
      const filename = `${type}_${year}-${month}.${ext}`;

      downloadBlob(blob, filename);
      return true;
    } catch (err: any) {
      throw err;
    }
  };

  const downloadFile = (filename: string) => {
    if (!fileUrl) return;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const cleanupFile = () => {
    if (fileUrl) {
      window.URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
      setFileContent(null);
    }
  };

  return {
    viewFile,
    downloadFile,
    cleanupFile,
    fileUrl,
    fileContent,
    isLoading,
    error,
    prepareExport,
  };
};
